import { useState, useEffect, useMemo, useRef } from 'react';
import { VisibleRows, VisibleColumns } from '../types';

interface UseVirtualizationProps {
    data: any[];
    columns: any[];
    rowHeight: number;
    headerHeight: number;
    maxVisibleColumns?: number;
    customWidths?: Record<string, number>;
}

export function useVirtualization({
    data,
    columns,
    rowHeight,
    headerHeight,
    maxVisibleColumns = 20,
    customWidths = {},
}: UseVirtualizationProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);

    const totalHeight = data.length * rowHeight;

    const visibleRows: VisibleRows = useMemo(() => {
        const overscan = 10;
        const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
        const endIndex = Math.min(data.length, Math.ceil((scrollTop + containerHeight) / rowHeight) + overscan);

        return {
            startIndex,
            endIndex,
            rows: data.slice(startIndex, endIndex),
        };
    }, [data, scrollTop, containerHeight, rowHeight]);

    const { visibleColumns, totalWidth, columnWidths } = useMemo(() => {
        if (containerWidth === 0) {
            return {
                visibleColumns: {
                    startIndex: 0,
                    endIndex: 0,
                    columns: [],
                    offsetLeft: 0,
                },
                totalWidth: 0,
                columnWidths: [],
            };
        }

        const maxCols = Math.min(maxVisibleColumns, columns.length);
        const defaultColumnWidth = containerWidth / maxCols;

        const columnWidthsArray = columns.map((col) => customWidths[col.name] ?? defaultColumnWidth);
        const totalW = columnWidthsArray.reduce((sum, width) => sum + width, 0);

        let accumulatedWidth = 0;
        let startIndex = 0;
        for (let i = 0; i < columns.length; i++) {
            if (accumulatedWidth + columnWidthsArray[i] > scrollLeft) {
                startIndex = i;
                break;
            }
            accumulatedWidth += columnWidthsArray[i];
        }

        let visibleWidth = 0;
        let endIndex = startIndex;
        for (let i = startIndex; i < columns.length; i++) {
            visibleWidth += columnWidthsArray[i];
            endIndex = i + 1;
            if (visibleWidth >= containerWidth) break;
        }

        const visibleCols = columns.slice(startIndex, endIndex);
        const widths = columnWidthsArray.slice(startIndex, endIndex);
        const offsetLeft = columnWidthsArray.slice(0, startIndex).reduce((sum, width) => sum + width, 0);

        const result: VisibleColumns = {
            startIndex,
            endIndex,
            columns: visibleCols,
            offsetLeft,
        };

        return {
            visibleColumns: result,
            totalWidth: totalW,
            columnWidths: widths,
        };
    }, [columns, scrollLeft, containerWidth, maxVisibleColumns, customWidths]);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        setScrollTop(target.scrollTop);
        setScrollLeft(target.scrollLeft);
    };

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
                setContainerHeight(containerRef.current.clientHeight - headerHeight);
            }
        };

        updateSize();
        const resizeObserver = new ResizeObserver(updateSize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [headerHeight]);

    return {
        containerRef,
        scrollTop,
        scrollLeft,
        totalHeight,
        totalWidth,
        visibleRows,
        visibleColumns,
        columnWidths,
        handleScroll,
    };
}
